/**
 * THE NOTIFICATIONS CARD. Phase 23.
 *
 * One card, opened from the More sheet on a phone and from the bell on the wide bar. It says
 * three things and offers three switches: whether this browser is subscribed, which of the
 * three reasons it will be woken for, and what the last few pushes were. The windows are
 * printed next to the switches, because "at most once in eight hours" is the promise that
 * makes a switch worth turning on.
 *
 * The browser's half (permission, the push service) lives in `pwa.ts`; the server's half (the
 * rows) in the store. This component only joins them.
 */

import { useEffect, useState } from 'react'
import type { PushKind } from '@ashport/shared'
import { JEST } from '../build'
import { Icon } from '../ui/Icon'
import { Btn, Row } from '../ui/Kit'
import { sz } from '../ui/viewport'
import { useSession } from '../game/session'
import { currentSubscription, PUSH_CHOICES, PUSH_LABEL, pushSupported } from '../pwa'
import {
  jestLogin,
  jestPlayerNow,
  jestReminders,
  jestRemindPref,
  jestShareReferral,
  jestSignIn,
  REMINDERS,
  setJestRemindPref,
} from '../jest'

const WHEN = (at: number) => {
  const d = new Date(at)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

/**
 * PHASE 26. Two cards behind one name. Under the Jest build the platform sends the texts and
 * there is no service worker to push to, so the card is the reminders card below (DESIGN-DELTAS
 * 1ag.7); everywhere else it is the push card it has been since Phase 23. `JEST` is a
 * compile-time constant, so each build carries one of them.
 */
export function Notifications({ onClose }: { onClose: () => void }) {
  return JEST ? <JestReminders onClose={onClose} /> : <PushCard onClose={onClose} />
}

/** The shell both cards share: the scrim, the plate, the bell and the close. */
function Card({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      role="dialog"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(0,0,0,.72)',
        zIndex: 90,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: '90dvh',
          overflowY: 'auto',
          background: 'var(--coal)',
          border: '1px solid var(--line-brass)',
          padding: '16px 18px 18px',
        }}
      >
        <Row gap={10} style={{ alignItems: 'center' }}>
          <Icon n="bell" s={16} c="var(--gold)" />
          <div className="t-head" style={{ flex: 1, fontSize: 15, color: 'var(--ink-hi)', letterSpacing: '.14em' }}>
            Notifications
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4, lineHeight: 0 }}
          >
            <Icon n="close" s={12} c="var(--ink-dim)" />
          </button>
        </Row>
        {children}
      </div>
    </div>
  )
}

/** The switch both cards draw. */
function Switch({ on, label, disabled, onClick }: { on: boolean; label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: 'none',
        width: 44,
        height: 26,
        borderRadius: 13,
        border: `1px solid ${on ? 'var(--gold)' : 'var(--line)'}`,
        background: on ? 'color-mix(in srgb, var(--gold) 35%, transparent)' : 'transparent',
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 21 : 3,
          width: 18,
          height: 18,
          borderRadius: 9,
          background: on ? 'var(--gold)' : 'var(--ink-dim)',
          transition: 'left .15s',
        }}
      />
    </button>
  )
}

/**
 * THE REMINDERS CARD. Phase 26, DESIGN-DELTAS 1ag.7 and 1ag.8.
 *
 * One switch, because the platform sends at most one text a day per player across every game
 * and chooses which; the game's part is to say whether it may, and to hold three short reasons
 * to come back at day one, three and seven. Registered players only, which the platform enforces
 * itself, so a guest is told once and offered the platform's own registration popup. The share
 * sheet is theirs too: "Bring a name in" is the game's voice for their referral link.
 *
 * Nothing here is server state. The switch is a browser preference; the schedule lives with the
 * platform and is re-applied on every ready boot from `App`.
 */
function JestReminders({ onClose }: { onClose: () => void }) {
  const playerId = useSession((s) => s.me?.playerId ?? '')
  const start = useSession((s) => s.start)
  const [on, setOn] = useState(jestRemindPref)
  const [registered, setRegistered] = useState(() => jestPlayerNow()?.registered === true)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const flip = () => {
    const next = !on
    setOn(next)
    setJestRemindPref(next)
    const n = jestReminders(next, registered)
    setNote(
      next
        ? registered
          ? n > 0
            ? 'Three reminders are with Jest: after a night, after three, after a week.'
            : 'The switch is on. Reminders will be set the next time the game opens on Jest.'
          : 'The switch is on. Reminders start once you have a Jest account.'
        : 'No reminders. The city will not text.',
    )
  }

  const register = () => {
    setBusy(true)
    setNote(null)
    void (async () => {
      const shown = await jestLogin({ route: 'home' })
      if (!shown) {
        setNote('The platform did not answer. Open the game from Jest.')
        return
      }
      // Sign in again with what the platform says now: a guest who registered keeps the row and
      // gains the name (1ag.1), and the server refreshes both from the new signature.
      const r = await jestSignIn()
      if (r === null) {
        setNote('Jest sent no player back. Open the game again.')
        return
      }
      await start()
      const now = jestPlayerNow()?.registered === true || r.registered
      setRegistered(now)
      if (now) {
        jestReminders(on, true)
        setNote(on ? 'Registered. Three reminders are with Jest.' : 'Registered. Turn the switch on to be reminded.')
      } else {
        setNote('Still a guest. Register with Jest to be reminded.')
      }
    })()
      .catch(() => setNote('That did not go through. Try again in a minute.'))
      .finally(() => setBusy(false))
  }

  const share = () => {
    setBusy(true)
    setNote(null)
    void jestShareReferral(playerId, { route: 'home' })
      .then((r) => setNote(r === null ? 'The platform did not answer.' : r.canceled ? 'Nothing sent.' : 'Sent. When they come in, you will know.'))
      .catch(() => setNote('That did not go through.'))
      .finally(() => setBusy(false))
  }

  return (
    <Card onClose={onClose}>
      <p style={{ margin: '10px 0 0', fontSize: sz(12), color: 'var(--ink-mid)', lineHeight: 1.5 }}>
        Jest sends the texts, never more than one a day, and only to a registered player. The
        city has three reasons to send one, and this is the one switch that allows it.
      </p>

      <div style={{ marginTop: 14, borderTop: '1px solid var(--line-soft)' }}>
        <Row gap={10} style={{ padding: '10px 0', borderBottom: '1px solid var(--line-soft)', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: sz(12.5), color: 'var(--ink-hi)' }}>Remind me to come back</div>
            <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', lineHeight: 1.4 }}>
              After a night, after three nights, after a week. Each one lands you on Jobs.
            </div>
          </div>
          <Switch on={on} label="Remind me to come back" disabled={busy} onClick={flip} />
        </Row>
        {REMINDERS.map((r) => (
          <Row key={r.identifier} gap={8} style={{ padding: '5px 0', fontSize: sz(11), alignItems: 'flex-start' }}>
            <span className="t-mono" style={{ color: 'var(--ink-faint)', flex: 'none', minWidth: 42 }}>
              day {r.scheduledInDays}
            </span>
            <span style={{ flex: 1, minWidth: 0, color: on ? 'var(--ink)' : 'var(--ink-dim)', lineHeight: 1.4 }}>{r.body}</span>
          </Row>
        ))}
      </div>

      {!registered && (
        <p style={{ margin: '12px 0 0', fontSize: sz(11.5), color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          You are playing as a guest. Reminders reach registered players, and registering keeps
          your progress on this account.
        </p>
      )}

      <Row gap={8} style={{ marginTop: 14, flexWrap: 'wrap' }}>
        {!registered && (
          <Btn v="gold" size="sm" disabled={busy} onClick={register} id="jest-register">
            Register with Jest
          </Btn>
        )}
        <Btn v={registered ? 'gold' : 'ghost'} size="sm" disabled={busy || playerId === ''} onClick={share} id="jest-share">
          Bring a name in
        </Btn>
      </Row>
      {note && (
        <div style={{ marginTop: 10, fontSize: sz(11.5), color: 'var(--ink-mid)', lineHeight: 1.45 }}>{note}</div>
      )}
    </Card>
  )
}

/** The push card, Phase 23, as it was. */
function PushCard({ onClose }: { onClose: () => void }) {
  const push = useSession((s) => s.push)
  const busy = useSession((s) => s.busy)
  const loadPush = useSession((s) => s.loadPush)
  const enablePush = useSession((s) => s.enablePush)
  const disablePush = useSession((s) => s.disablePush)
  const setPushKinds = useSession((s) => s.setPushKinds)
  const pingPush = useSession((s) => s.pingPush)
  const pushNote = useSession((s) => s.pushNote)
  const [endpoint, setEndpoint] = useState<string | null>(null)

  useEffect(() => {
    void currentSubscription().then((sub) => setEndpoint(sub?.endpoint ?? null))
    void loadPush()
  }, [loadPush])

  const mine = push?.subscriptions.find((s) => s.mine) ?? null
  const others = (push?.subscriptions ?? []).filter((s) => !s.mine)
  const configured = (push?.publicKey ?? '') !== ''
  const supported = pushSupported()
  const kinds = new Set<PushKind>(mine?.kinds ?? PUSH_CHOICES)

  const toggle = (k: PushKind) => {
    const next = new Set(kinds)
    if (next.has(k)) next.delete(k)
    else next.add(k)
    void setPushKinds([...next])
  }

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(0,0,0,.72)',
        zIndex: 90,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: '90dvh',
          overflowY: 'auto',
          background: 'var(--coal)',
          border: '1px solid var(--line-brass)',
          padding: '16px 18px 18px',
        }}
      >
        <Row gap={10} style={{ alignItems: 'center' }}>
          <Icon n="bell" s={16} c="var(--gold)" />
          <div className="t-head" style={{ flex: 1, fontSize: 15, color: 'var(--ink-hi)', letterSpacing: '.14em' }}>
            Notifications
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4, lineHeight: 0 }}
          >
            <Icon n="close" s={12} c="var(--ink-dim)" />
          </button>
        </Row>

        <p style={{ margin: '10px 0 0', fontSize: sz(12), color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          Three reasons to come back, and they arrive on their own. Each one is capped, so the
          city never nags: the windows are printed under the switches.
        </p>

        {push !== null && !configured && (
          <p style={{ margin: '12px 0 0', fontSize: sz(11.5), color: 'var(--heat)', lineHeight: 1.5 }}>
            Push is not set up on this deployment yet. The switches will work once it is.
          </p>
        )}
        {configured && !supported && (
          <p style={{ margin: '12px 0 0', fontSize: sz(11.5), color: 'var(--heat)', lineHeight: 1.5 }}>
            This browser cannot take a push. On an iPhone, add the game to the home screen from
            the share sheet, then open it from there.
          </p>
        )}

        <div style={{ marginTop: 14, borderTop: '1px solid var(--line-soft)' }}>
          {PUSH_CHOICES.map((k) => {
            const on = mine ? kinds.has(k) : false
            return (
              <Row key={k} gap={10} style={{ padding: '10px 0', borderBottom: '1px solid var(--line-soft)', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: sz(12.5), color: 'var(--ink-hi)' }}>{PUSH_LABEL[k].title}</div>
                  <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', lineHeight: 1.4 }}>{PUSH_LABEL[k].line}</div>
                </div>
                <button
                  role="switch"
                  aria-checked={on}
                  aria-label={PUSH_LABEL[k].title}
                  disabled={!mine || busy}
                  onClick={() => toggle(k)}
                  style={{
                    flex: 'none',
                    width: 44,
                    height: 26,
                    borderRadius: 13,
                    border: `1px solid ${on ? 'var(--gold)' : 'var(--line)'}`,
                    background: on ? 'color-mix(in srgb, var(--gold) 35%, transparent)' : 'transparent',
                    position: 'relative',
                    cursor: mine ? 'pointer' : 'default',
                    opacity: mine ? 1 : 0.5,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 3,
                      left: on ? 21 : 3,
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      background: on ? 'var(--gold)' : 'var(--ink-dim)',
                      transition: 'left .15s',
                    }}
                  />
                </button>
              </Row>
            )
          })}
        </div>

        <Row gap={8} style={{ marginTop: 14, flexWrap: 'wrap' }}>
          {mine ? (
            <>
              <Btn v="ghost" size="sm" disabled={busy} onClick={() => void disablePush()}>
                Turn off on this device
              </Btn>
              <Btn v="gold" size="sm" disabled={busy} onClick={() => void pingPush()}>
                Send a test
              </Btn>
            </>
          ) : (
            <Btn
              v="gold"
              size="sm"
              disabled={busy || !configured || !supported}
              onClick={() => void enablePush(PUSH_CHOICES)}
              id="push-enable"
            >
              Turn on for this device
            </Btn>
          )}
        </Row>
        {pushNote && (
          <div style={{ marginTop: 10, fontSize: sz(11.5), color: 'var(--ink-mid)', lineHeight: 1.45 }}>{pushNote}</div>
        )}
        {mine && (
          <div className="t-mono" style={{ marginTop: 8, fontSize: sz(10), color: 'var(--ink-faint)' }}>
            This device, via {mine.service.replace(/^https?:\/\//, '')}
            {endpoint ? '' : ' (the browser has no subscription of its own yet)'}
          </div>
        )}
        {others.length > 0 && (
          <div className="t-mono" style={{ marginTop: 4, fontSize: sz(10), color: 'var(--ink-faint)' }}>
            {others.length} other {others.length === 1 ? 'device' : 'devices'} on this account.
          </div>
        )}

        {push && push.recent.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div className="t-label" style={{ fontSize: sz(9), color: 'var(--ink-faint)', letterSpacing: '.2em' }}>
              Recent
            </div>
            {push.recent.map((r) => (
              <Row key={`${r.kind}-${r.at}`} gap={8} style={{ padding: '4px 0', fontSize: sz(11) }}>
                <span className="t-mono" style={{ color: 'var(--ink-faint)', flex: 'none' }}>{WHEN(r.at)}</span>
                <span style={{ flex: 1, minWidth: 0, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.title}
                </span>
                <span className="t-mono" style={{ color: r.state === 'sent' ? 'var(--dirty)' : 'var(--ink-dim)', flex: 'none' }}>
                  {r.state}
                </span>
              </Row>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** "A new build is ready." Shown by the shell when the worker installed beside the old one. */
export function UpdateBar({ onReload, onLater }: { onReload: () => void; onLater: () => void }) {
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
        zIndex: 85,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: 'var(--coal)',
        border: '1px solid var(--line-brass)',
        maxWidth: 520,
        margin: '0 auto',
      }}
    >
      <Icon n="bell" s={14} c="var(--gold)" />
      <span style={{ flex: 1, fontSize: sz(12), color: 'var(--ink-hi)' }}>A new build of Ashport is ready.</span>
      <Btn v="ghost" size="sm" onClick={onLater}>
        Later
      </Btn>
      <Btn v="gold" size="sm" onClick={onReload}>
        Reload
      </Btn>
    </div>
  )
}
